import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { DevCycleProvider, useIsDevCycleInitialized } from '@devcycle/react-client-sdk';
import { useAuth } from '@/api';
import { useMemo } from 'react';
import { getDevCycleConfig } from '@/config/devcycle';
import { AppShell, PageLoadingState } from '@/components/layout';

// Get DevCycle configuration
const devCycleConfig = getDevCycleConfig();
const isDevCycleEnabled = Boolean(devCycleConfig.sdkKey);

/**
 * Authenticated Layout Route
 * All routes under this layout require authentication
 * Uses TanStack Router's beforeLoad for auth checking
 *
 * DevCycle is initialized here (not at app root) to:
 * - Avoid bot traffic counting against quota
 * - Ensure user ID is available for targeting
 * - Only load feature flags for authenticated users
 */
export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ context }) => {
    // If still loading, allow render - component will show a loading spinner
    if (context.auth.isLoading) {
      return;
    }

    // Let unauthenticated users choose email or social sign-in.
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: '/',
      });
    }
  },
  component: AuthenticatedLayout,
});

/**
 * Waits for DevCycle to initialize before rendering children.
 * Shows loading spinner while DevCycle loads (typically <100ms)
 */
function DevCycleGate({ children }: { children: React.ReactNode }) {
  const isReady = useIsDevCycleInitialized();

  if (!isReady) {
    return <PageLoadingState />;
  }

  return <>{children}</>;
}

/**
 * Inner content — AppShell wraps all authenticated pages.
 * PageTransition provides route-change animations.
 * Separated so the DevCycleProvider's hook (useIsDevCycleInitialized) works.
 */
function AuthenticatedContent() {
  return (
    <DevCycleGate>
      <AppShell>
        <Outlet />
      </AppShell>
    </DevCycleGate>
  );
}

function AuthenticatedLayout() {
  const { user, isAuthenticated, isLoading } = useAuth();

  // Memoize DevCycle config to prevent unnecessary re-renders
  // Dependencies are granular to avoid rebuilding on unrelated user property changes
  const devCycleProviderConfig = useMemo(() => {
    if (!user || !devCycleConfig.sdkKey) return null;

    return {
      sdkKey: devCycleConfig.sdkKey,
      user: {
        user_id: user.id,
        email: user.email,
        customData: {
          platform: 'web' as const,
        },
      },
      options: devCycleConfig.options,
    };
  }, [user]);

  // Show loading spinner while checking auth or if not authenticated
  // (beforeLoad handles redirect for unauthenticated users)
  if (isLoading || !isAuthenticated) {
    return <PageLoadingState />;
  }

  // If DevCycle is enabled and we have a valid config, wrap content with provider
  if (isDevCycleEnabled && devCycleProviderConfig) {
    return (
      <DevCycleProvider config={devCycleProviderConfig}>
        <AuthenticatedContent />
      </DevCycleProvider>
    );
  }

  // Render without feature flags
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
